import { FileText, Network, ScrollText } from 'lucide-react';
import { useLang, type Lang, type StringTable } from '@/lib/i18n';

const STRINGS = {
  'preview.eyebrow': { en: 'Inside the command center', fa: 'درونِ مرکز فرماندهی' },
  'preview.title.lead': {
    en: 'Streaming, sourced,',
    fa: 'جاری، مستند،',
  },
  'preview.title.accent': { en: 'shown — not summarized', fa: 'نمایش‌داده‌شده — نه خلاصه' },
  'preview.title.tail': { en: '.', fa: '.' },
  'preview.subtitle': {
    en: 'A still frame of the analyst surface. The model writes one section at a time, each with its own confidence and inline citations. Evidence stays open beside the answer — never hidden behind a "show source" toggle.',
    fa: 'یک قابِ ثابت از سطحِ تحلیل‌گر. مدل، بخش به بخش می‌نویسد و هر بخش، نمایش‌گرِ اطمینان و ارجاع‌های درون‌متنیِ خود را دارد. شواهد در کنارِ پاسخ باز می‌مانند — هرگز پشتِ کلیدِ «نمایشِ منبع» پنهان نمی‌شوند.',
  },
  'preview.question.label': { en: 'Question', fa: 'پرسش' },
  'preview.question.text': {
    en: 'What instruments connect Bank Mellat to the FATF gray-list deliberations of 2023?',
    fa: 'کدام ابزارها بانکِ ملت را به مذاکراتِ فهرستِ خاکستریِ FATF در سال ۲۰۲۳ پیوند می‌دهند؟',
  },
  'preview.streaming.label': {
    en: 'Streaming · section 2 of 4',
    fa: 'در حالِ پخش · بخشِ ۲ از ۴',
  },
  'preview.streaming.body.a': {
    en: 'Three correspondent-banking instruments tie',
    fa: 'سه ابزارِ بانکداریِ کارگزار',
  },
  'preview.streaming.body.entity': { en: 'Bank Mellat', fa: 'بانکِ ملت' },
  'preview.streaming.body.b': {
    en: 'to the deliberations: a 2022 dollar-clearing arrangement',
    fa: 'را به مذاکرات پیوند می‌دهند: یک ترتیبِ تسویهٔ دلاری در سالِ ۲۰۲۲',
  },
  'preview.streaming.body.cite1': { en: 'CABLE-1144', fa: 'CABLE-1144' },
  'preview.streaming.body.c': {
    en: ', a Hawala-network exposure flagged by FIU',
    fa: '، اکسپوژرِ شبکهٔ حواله که توسط FIU علامت‌گذاری شد',
  },
  'preview.streaming.body.cite2': { en: 'FIU-22-008', fa: 'FIU-22-008' },
  'preview.streaming.body.d': {
    en: ', and a 2023 SWIFT message volume anomaly',
    fa: '، و ناهنجاریِ حجمِ پیامِ سوئیفت در سالِ ۲۰۲۳',
  },
  'preview.streaming.body.cite3': { en: 'OBS-2023-Q2', fa: 'OBS-2023-Q2' },
  'preview.confidence.label': { en: 'Confidence', fa: 'اطمینان' },
  'preview.confidence.value': { en: '0.78', fa: '۰٫۷۸' },
  'preview.confidence.note': {
    en: 'Three independent sources, one inferred linkage.',
    fa: 'سه منبعِ مستقل، یک پیوندِ استنتاجی.',
  },
  'preview.evidence.label': { en: 'Evidence trail', fa: 'ردِ شواهد' },
  'preview.evidence.count': { en: '3 sources cited', fa: '۳ منبعِ ارجاع‌شده' },
  'preview.network.label': { en: 'Network neighborhood', fa: 'همسایگیِ شبکه' },
  'preview.network.entity': { en: 'Bank Mellat', fa: 'بانکِ ملت' },
  'preview.network.facets': {
    en: '5 instruments · 12 counterparties',
    fa: '۵ ابزار · ۱۲ طرفِ معامله',
  },
  'src.cable.label': { en: 'Diplomatic cable', fa: 'تلگرامِ دیپلماتیک' },
  'src.cable.title': {
    en: 'Tehran → Vienna · clearing channel review',
    fa: 'تهران ← وین · بازنگری در کانالِ تسویه',
  },
  'src.fiu.label': { en: 'Regulatory filing', fa: 'گزارشِ نظارتی' },
  'src.fiu.title': {
    en: 'FIU watch-list addendum · Q4 2022',
    fa: 'پیوستِ فهرستِ پایشِ FIU · سه‌ماههٔ چهارم ۲۰۲۲',
  },
  'src.obs.label': { en: 'Internal observation', fa: 'مشاهدهٔ داخلی' },
  'src.obs.title': {
    en: 'SWIFT volume anomaly · April–June 2023',
    fa: 'ناهنجاریِ حجمِ سوئیفت · فروردین–خرداد ۱۴۰۲',
  },
} satisfies StringTable<string>;

type SourceKey = 'cable' | 'fiu' | 'obs';
const SOURCE_IDS: Record<SourceKey, string> = {
  cable: 'CABLE-1144',
  fiu: 'FIU-22-008',
  obs: 'OBS-2023-Q2',
};
const SOURCES: { key: SourceKey; icon: typeof FileText }[] = [
  { key: 'cable', icon: FileText },
  { key: 'fiu', icon: ScrollText },
  { key: 'obs', icon: Network },
];

function CitationPill({ id }: { id: string }) {
  return (
    <span className="mx-0.5 inline-flex items-baseline gap-1 rounded-xs border border-[var(--color-accent)] bg-[var(--color-accent-softer)] px-1.5 py-0.5 align-baseline font-mono text-2xs uppercase tracking-wider text-[var(--color-accent)]">
      {id}
    </span>
  );
}

function ConfidenceMeter({ value }: { value: number }) {
  // value 0..1
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div
      className="relative h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-elevated)]"
      role="img"
      aria-label={`Confidence ${value.toFixed(2)}`}
    >
      <div
        className="absolute inset-y-0 start-0 bg-[var(--color-accent-bright)]"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function LivePreview() {
  const lang = useLang();
  const pickKey = (
    key: keyof typeof STRINGS,
    l: Lang = lang,
  ): string => STRINGS[key][l];

  return (
    <section className="border-t border-[var(--color-border)] bg-[var(--color-canvas)]">
      <div className="mx-auto max-w-[88rem] px-6 py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="mb-4 text-xs uppercase tracking-wider text-[var(--color-ink-muted)]">
            {pickKey('preview.eyebrow')}
          </div>
          <h2 className="text-3xl leading-tight md:text-5xl">
            {pickKey('preview.title.lead')}{' '}
            <span className="text-[var(--color-accent)]">
              {pickKey('preview.title.accent')}
            </span>
            {pickKey('preview.title.tail')}
          </h2>
          <p className="mt-4 text-[var(--color-ink-secondary)] md:text-lg">
            {pickKey('preview.subtitle')}
          </p>
        </div>

        {/* Mock command-center surface */}
        <div className="glass-card mt-12 overflow-hidden rounded-lg">
          {/* Window chrome */}
          <div className="flex items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-elevated)] px-4 py-2.5">
            <div className="flex gap-1.5">
              <span className="size-2.5 rounded-full bg-[var(--color-border)]" />
              <span className="size-2.5 rounded-full bg-[var(--color-border)]" />
              <span className="size-2.5 rounded-full bg-[var(--color-border)]" />
            </div>
            <span className="font-mono text-2xs uppercase tracking-wider text-[var(--color-ink-muted)]">
              jam-e-jam · command center
            </span>
          </div>

          <div className="grid gap-0 md:grid-cols-[1.6fr_1fr]">
            {/* Streaming answer column */}
            <div className="border-b border-[var(--color-border)] p-6 md:border-b-0 md:border-e">
              <div className="mb-3 flex items-center gap-2 text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
                <span className="font-mono">{pickKey('preview.question.label')}</span>
                <span className="h-px flex-1 bg-[var(--color-border)]" />
              </div>
              <p className="font-serif text-lg leading-snug text-[var(--color-ink)] md:text-xl">
                {pickKey('preview.question.text')}
              </p>

              <div className="mt-6 flex items-center gap-2 text-xxs uppercase tracking-wider text-[var(--color-accent-bright)]">
                <span className="size-1.5 animate-pulse rounded-full bg-[var(--color-accent-bright)]" />
                <span className="font-mono">
                  {pickKey('preview.streaming.label')}
                </span>
              </div>

              <p className="mt-3 text-md leading-relaxed text-[var(--color-ink-secondary)]">
                <span className="streaming-token" style={{ animationDelay: '0ms' }}>
                  {pickKey('preview.streaming.body.a')}{' '}
                </span>
                <span
                  className="streaming-token font-medium text-[var(--color-ink)]"
                  style={{ animationDelay: '120ms' }}
                >
                  <bdi>{pickKey('preview.streaming.body.entity')}</bdi>
                </span>
                <span className="streaming-token" style={{ animationDelay: '260ms' }}>
                  {' '}{pickKey('preview.streaming.body.b')}
                </span>
                <span className="streaming-token" style={{ animationDelay: '400ms' }}>
                  {' '}<CitationPill id={SOURCE_IDS.cable} />
                </span>
                <span className="streaming-token" style={{ animationDelay: '540ms' }}>
                  {pickKey('preview.streaming.body.c')}
                </span>
                <span className="streaming-token" style={{ animationDelay: '680ms' }}>
                  {' '}<CitationPill id={SOURCE_IDS.fiu} />
                </span>
                <span className="streaming-token" style={{ animationDelay: '820ms' }}>
                  {pickKey('preview.streaming.body.d')}
                </span>
                <span className="streaming-token" style={{ animationDelay: '960ms' }}>
                  {' '}<CitationPill id={SOURCE_IDS.obs} />
                </span>
                <span className="streaming-caret" aria-hidden />
              </p>

              <div className="mt-6 rounded-sm border border-[var(--color-border)] bg-[var(--color-canvas)] p-4">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-mono text-2xs uppercase tracking-wider text-[var(--color-ink-muted)]">
                    {pickKey('preview.confidence.label')}
                  </span>
                  <span className="font-mono text-sm tabular-nums text-[var(--color-ink)]">
                    {pickKey('preview.confidence.value')}
                  </span>
                </div>
                <div className="mt-2">
                  <ConfidenceMeter value={0.78} />
                </div>
                <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
                  {pickKey('preview.confidence.note')}
                </p>
              </div>
            </div>

            {/* Evidence trail column */}
            <aside className="bg-[var(--color-canvas)] p-6">
              <div className="mb-4 flex items-baseline justify-between">
                <div className="text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
                  {pickKey('preview.evidence.label')}
                </div>
                <div className="font-mono text-2xs uppercase tracking-wider text-[var(--color-accent-bright)]">
                  {pickKey('preview.evidence.count')}
                </div>
              </div>

              <ul className="flex flex-col gap-3">
                {SOURCES.map(({ key, icon: Icon }) => {
                  const labelKey = `src.${key}.label` as keyof typeof STRINGS;
                  const titleKey = `src.${key}.title` as keyof typeof STRINGS;
                  return (
                    <li
                      key={key}
                      className="flex items-start gap-3 rounded-sm border border-[var(--color-border)] bg-[var(--color-surface)] p-3"
                    >
                      <div className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-xs border border-[var(--color-border)] bg-[var(--color-elevated)] text-[var(--color-accent-bright)]">
                        <Icon className="size-3.5" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-mono text-2xs uppercase tracking-wider text-[var(--color-accent)]">
                          {SOURCE_IDS[key]}
                        </div>
                        <div className="text-xs text-[var(--color-ink-muted)]">
                          {pickKey(labelKey)}
                        </div>
                        <div className="mt-0.5 text-sm text-[var(--color-ink)]">
                          {pickKey(titleKey)}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-5 rounded-sm border border-dashed border-[var(--color-border)] p-3">
                <div className="text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
                  {pickKey('preview.network.label')}
                </div>
                <div className="mt-1 font-serif text-md text-[var(--color-ink)]">
                  <bdi>{pickKey('preview.network.entity')}</bdi>
                </div>
                <div className="mt-1 text-xs text-[var(--color-ink-secondary)]">
                  {pickKey('preview.network.facets')}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}
